// Single fetch wrapper for the SPA. Same-origin only — cookies do the auth.

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Anything under /api/auth/* legitimately operates without a session
// (login, accept-invite, forgot-password, reset-password, me-probe).
// 401s there are meaningful UI states, not "session expired" — never
// auto-redirect, or the recovery flows themselves would bounce to /login.
function isAuthFlowPath(path: string): boolean {
  // Strip query string so /api/auth/me?foo=bar still matches.
  const bare = path.split("?", 1)[0] ?? path;
  return bare.startsWith("/api/auth/");
}

function handleUnauthorized(path: string): void {
  if (isAuthFlowPath(path)) return;

  // Avoid redirect loops if we're already on the auth screen.
  if (window.location.pathname.startsWith("/login")) return;

  // Best-effort: clear the workspace selector cache so the next login
  // doesn't immediately bounce into a stale workspace.
  try {
    window.localStorage.removeItem("ml.current-workspace-slug");
  } catch {
    // Storage may be disabled in some browser modes — ignore.
  }

  // Preserve where we were so login can redirect back.
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

interface RequestInitWithJson extends RequestInit {
  json?: unknown;
  params?: Record<string, string | number | string[] | undefined>;
}

function buildUrl(path: string, params?: RequestInitWithJson["params"]): string {
  if (!params) return path;
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    // Arrays become repeated params (?k=a&k=b) so values containing commas
    // (e.g. a "City, State" location string) survive intact.
    if (Array.isArray(v)) {
      for (const item of v) url.searchParams.append(k, String(item));
    } else {
      url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

async function request<T>(path: string, init: RequestInitWithJson = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const res = await fetch(buildUrl(path, init.params), {
    ...init,
    headers,
    body,
    credentials: "same-origin",
  });
  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized(path);
    }
    throw new ApiError(
      (data && typeof data === "object" && "error" in data && typeof (data as { error: unknown }).error === "string"
        ? (data as { error: string }).error
        : `HTTP ${res.status}`),
      res.status,
      data
    );
  }
  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const apiClient = {
  get: <T>(path: string, init?: RequestInitWithJson) =>
    request<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, init?: RequestInitWithJson) =>
    request<T>(path, { ...init, method: "POST" }),
  put: <T>(path: string, init?: RequestInitWithJson) =>
    request<T>(path, { ...init, method: "PUT" }),
  patch: <T>(path: string, init?: RequestInitWithJson) =>
    request<T>(path, { ...init, method: "PATCH" }),
  delete: <T>(path: string, init?: RequestInitWithJson) =>
    request<T>(path, { ...init, method: "DELETE" }),
};
